import React from 'react';
import {
  PageHeader, PageTitle, Card, CardGrid, AlertBanner, Badge,
  Table, Thead, Tbody,
  ModalOverlay, ModalDialog, ModalTitle, ModalBody, ModalActions,
  PrimaryButton, SecondaryButton, DestructiveButton,
} from '../styled-components';
import { useFetchData } from '../hooks';
import { CSRF_HEADER_NAME, getCSRFToken } from "../../common/utils";
import { CreditCard, TrendingUp, CheckCircle } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BillingStatus {
  billing_model: string;
  billing_tier: string | null;
  period_start: string | null;
  period_end: string | null;
  pending_tier: string | null;
  minutes_used: number | null;
  included_minutes: number | null;
  overage_minutes: number | null;
  overage_estimate_cents: number | null;
  payment_method_on_file: boolean;
  grace_period_end: string | null;
  status: 'active' | 'past_due' | 'suspended';
}

interface TierConfig {
  id: string;
  name: string;
  monthly_fee_dollars: number;
  included_minutes: number;
  overage_rate_dollars: number;
}

interface BillingTiers {
  tiers: TierConfig[];
}

interface UsageDay {
  date: string;
  minutes: number;
}

interface BillingUsage {
  days: UsageDay[];
}

interface PaymentRecord {
  id: string;
  amount_cents: number;
  currency: string;
  status: string;
  payment_type: string | null;
  period_start: string | null;
  created_at: string | null;
}

interface BillingPayments {
  payments: PaymentRecord[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function pct(used: number, included: number): number {
  return Math.min(100, Math.round((used / included) * 100));
}

// ---------------------------------------------------------------------------
// Usage progress bar
// ---------------------------------------------------------------------------

function UsageBar({ used, included }: { used: number; included: number }) {
  const p = pct(used, included);
  const color = p >= 100 ? 'var(--accent-red)' : p >= 80 ? 'var(--accent-amber)' : 'var(--accent-cyan)';
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{
        height: 6,
        background: 'var(--bg-base)',
        borderRadius: 3,
        overflow: 'hidden',
      }}>
        <div style={{
          width: `${p}%`,
          height: '100%',
          background: color,
          borderRadius: 3,
          transition: 'width 600ms cubic-bezier(0.4,0,0.2,1)',
        }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
        <span>{used.toLocaleString()} min used</span>
        <span>{included.toLocaleString()} min included</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Current plan card
// ---------------------------------------------------------------------------

function pendingLabel(tiers: TierConfig[], pendingTier: string): string {
  if (pendingTier === 'cancelled') return 'Cancelling';
  return tiers.find((t) => t.id === pendingTier)?.name ?? pendingTier;
}

function CurrentPlanCard({ status, tiers }: { status: BillingStatus; tiers: TierConfig[] }) {
  const tierConfig = tiers.find((t) => t.id === status.billing_tier);
  const tierName = tierConfig?.name ?? status.billing_tier ?? '—';
  const tierOrder = tiers.map((t) => t.id);
  const isPendingUpgrade = status.pending_tier != null
    && status.pending_tier !== 'cancelled'
    && tierOrder.indexOf(status.pending_tier) > tierOrder.indexOf(status.billing_tier ?? '');

  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary)', marginBottom: 4 }}>
            Current Plan
          </div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)' }}>{tierName}</div>
          {tierConfig && (
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>
              ${tierConfig.monthly_fee_dollars.toFixed(0)}/mo · {tierConfig.included_minutes.toLocaleString()} min/yr
            </div>
          )}
          {status.pending_tier && isPendingUpgrade && (
            <div style={{ fontSize: 12, color: 'var(--accent-cyan)', marginTop: 6 }}>
              Upgrading to {pendingLabel(tiers, status.pending_tier)}…
            </div>
          )}
          {status.pending_tier && !isPendingUpgrade && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
              {status.pending_tier === 'cancelled' ? 'Cancelling' : `Changing to ${pendingLabel(tiers, status.pending_tier)}`}
              {status.period_end ? ` on ${status.period_end}` : ' at end of billing period'}
            </div>
          )}
        </div>
        <Badge variant={status.status === 'active' ? 'active' : status.status === 'past_due' ? 'paused' : 'error'}>
          {status.status === 'active' ? 'Active' : status.status === 'past_due' ? 'Past Due' : 'Suspended'}
        </Badge>
      </div>

      {status.minutes_used !== null && status.included_minutes !== null && (
        <UsageBar used={status.minutes_used} included={status.included_minutes} />
      )}

      {status.overage_minutes !== null && status.overage_minutes > 0 && (
        <div style={{ marginTop: 12, padding: '8px 12px', background: 'var(--bg-base)', borderRadius: 6, fontSize: 13, color: 'var(--text-secondary)' }}>
          <span style={{ color: 'var(--accent-amber)', fontWeight: 600 }}>{status.overage_minutes.toLocaleString()} overage minutes</span>
          {status.overage_estimate_cents !== null && (
            <span> · est. {formatDollars(status.overage_estimate_cents)} additional charge</span>
          )}
        </div>
      )}

      {status.period_start && (
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)' }}>
          Monthly billing started {status.period_start}
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Payment method card
// ---------------------------------------------------------------------------

function PaymentMethodCard({ hasPaymentMethod }: { hasPaymentMethod: boolean }) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleUpdatePaymentMethod = async () => {
    setLoading(true);
    setError(null);
    let redirectUrl: string | undefined;
    try {
      const res = await fetch('/dashboard/api/billing/update-payment-method', {
        method: 'POST',
        headers: { [CSRF_HEADER_NAME]: await getCSRFToken() },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.detail ?? 'Something went wrong. Please try again.');
      } else {
        const body = await res.json();
        redirectUrl = body.redirect_url;
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      if (redirectUrl) window.location.href = redirectUrl;
      else setLoading(false);
    }
  };

  return (
    <Card>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary)', marginBottom: 12 }}>
        Payment Method
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <CreditCard size={20} style={{ color: hasPaymentMethod ? 'var(--accent-cyan)' : 'var(--text-muted)', flexShrink: 0 }} />
        <div style={{ fontSize: 14, color: hasPaymentMethod ? 'var(--text-primary)' : 'var(--text-muted)', flex: 1 }}>
          {hasPaymentMethod ? 'Card on file' : 'No payment method on file'}
        </div>
        <button
          onClick={handleUpdatePaymentMethod}
          disabled={loading}
          style={{ fontSize: 12, color: 'var(--accent-cyan)', background: 'none', border: '1px solid var(--accent-cyan)', borderRadius: 4, padding: '4px 10px', cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1, whiteSpace: 'nowrap' }}
        >
          {loading ? 'Loading…' : hasPaymentMethod ? 'Update' : 'Add'}
        </button>
      </div>
      {error && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--accent-red)' }}>{error}</div>}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Tier table
// ---------------------------------------------------------------------------

interface TierTableProps {
  tiers: TierConfig[];
  status: BillingStatus;
  actionLoading: boolean;
  onChangeTier: (tierId: string) => void;
  onCancel: () => void;
}

function TierTable({ tiers, status, actionLoading, onChangeTier, onCancel }: TierTableProps) {
  const tierOrder = tiers.map((t) => t.id);
  const currentIndex = tierOrder.indexOf(status.billing_tier ?? '');
  const hasPending = status.pending_tier != null;

  return (
    <Card style={{ marginTop: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary)', marginBottom: 16 }}>
        Plans
      </div>
      <Table>
        <Thead>
          <tr>
            <th>Plan</th>
            <th>Price</th>
            <th>Included Minutes</th>
            <th>Overage Rate</th>
            <th></th>
          </tr>
        </Thead>
        <Tbody>
          {tiers.map((tier, idx) => {
            const isCurrent = tier.id === status.billing_tier;
            const isPendingTarget = tier.id === status.pending_tier;
            const isUpgrade = idx > currentIndex;
            const isDowngrade = idx < currentIndex;

            let action: React.ReactNode = null;
            if (isCurrent) {
              action = <span style={{ fontSize: 12, color: 'var(--accent-cyan)', display: 'flex', alignItems: 'center', gap: 4 }}><CheckCircle size={13} /> Current</span>;
            } else if (isPendingTarget && isUpgrade) {
              action = <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Processing…</span>;
            } else if (isPendingTarget && isDowngrade) {
              action = <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Scheduled</span>;
            } else if (isUpgrade) {
              action = (
                <button
                  disabled={actionLoading || hasPending}
                  onClick={() => onChangeTier(tier.id)}
                  style={{ fontSize: 12, color: 'var(--accent-cyan)', background: 'none', border: 'none', cursor: actionLoading || hasPending ? 'default' : 'pointer', opacity: hasPending ? 0.4 : 1, padding: 0 }}
                >
                  Upgrade
                </button>
              );
            } else if (isDowngrade) {
              action = (
                <button
                  disabled={actionLoading || hasPending}
                  onClick={() => onChangeTier(tier.id)}
                  style={{ fontSize: 12, color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: actionLoading || hasPending ? 'default' : 'pointer', opacity: hasPending ? 0.4 : 1, padding: 0 }}
                >
                  Downgrade
                </button>
              );
            }

            return (
              <tr key={tier.id} style={{ cursor: 'default' }}>
                <td><span style={{ fontWeight: isCurrent ? 600 : 400 }}>{tier.name}</span></td>
                <td>${tier.monthly_fee_dollars.toFixed(0)}/mo</td>
                <td>{tier.included_minutes.toLocaleString()} min/yr</td>
                <td>${tier.overage_rate_dollars.toFixed(2)}/min</td>
                <td>{action}</td>
              </tr>
            );
          })}
        </Tbody>
      </Table>
      {status.billing_tier && status.billing_tier !== 'cancelled' && !hasPending && (
        <div style={{ marginTop: 12, textAlign: 'right' }}>
          <button
            disabled={actionLoading}
            onClick={onCancel}
            style={{ fontSize: 12, color: 'var(--text-muted)', background: 'none', border: 'none', cursor: actionLoading ? 'default' : 'pointer', textDecoration: 'underline', padding: 0 }}
          >
            Cancel plan
          </button>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Usage history sparkline (simple bar chart)
// ---------------------------------------------------------------------------

function UsageHistory({ usage }: { usage: BillingUsage }) {
  const days = usage.days.slice(-30);
  const maxMinutes = Math.max(...days.map((d) => d.minutes), 1);

  return (
    <Card style={{ marginTop: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary)' }}>
          Daily Usage (last 30 days)
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)' }}>
          <TrendingUp size={13} />
          {days.reduce((s, d) => s + d.minutes, 0).toLocaleString()} min total
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 64 }}>
        {days.map((d) => {
          const h = maxMinutes > 0 ? Math.max(2, Math.round((d.minutes / maxMinutes) * 60)) : 2;
          return (
            <div
              key={d.date}
              title={`${d.date}: ${d.minutes} min`}
              style={{
                flex: 1,
                height: h,
                background: 'var(--accent-cyan)',
                opacity: 0.6,
                borderRadius: '2px 2px 0 0',
                minWidth: 4,
              }}
            />
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11, color: 'var(--text-muted)' }}>
        <span>{days[0]?.date ?? ''}</span>
        <span>{days[days.length - 1]?.date ?? ''}</span>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Payment history table
// ---------------------------------------------------------------------------

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  platform_fee: 'Platform fee',
  overage: 'Overage',
  upgrade: 'Plan upgrade',
};

function formatPaymentType(type: string | null): string {
  if (!type) return 'Other';
  return PAYMENT_TYPE_LABELS[type] ?? 'Other';
}

function PaymentHistory({ payments }: { payments: PaymentRecord[] }) {
  if (payments.length === 0) return null;

  return (
    <Card style={{ marginTop: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-secondary)', marginBottom: 16 }}>
        Payment History
      </div>
      <Table>
        <Thead>
          <tr>
            <th>Date</th>
            <th>Description</th>
            <th>Amount</th>
            <th>Status</th>
          </tr>
        </Thead>
        <Tbody>
          {payments.map((p) => {
            const succeeded = p.status === 'succeeded';
            const date = p.created_at ? new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
            const amount = `$${(p.amount_cents / 100).toFixed(2)}`;
            return (
              <tr key={p.id} style={{ cursor: 'default' }}>
                <td style={{ color: 'var(--text-secondary)' }}>{date}</td>
                <td>{formatPaymentType(p.payment_type)}</td>
                <td>{amount}</td>
                <td>
                  <span style={{ color: succeeded ? 'var(--accent-green)' : 'var(--accent-red)', fontWeight: 500 }}>
                    {succeeded ? '✓ Paid' : '✗ Failed'}
                  </span>
                </td>
              </tr>
            );
          })}
        </Tbody>
      </Table>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Billing page
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Confirm modal
// ---------------------------------------------------------------------------

type PendingAction =
  | { type: 'upgrade'; tierId: string }
  | { type: 'downgrade'; tierId: string }
  | { type: 'cancel' };

interface ConfirmModalProps {
  action: PendingAction;
  tiers: TierConfig[];
  periodEnd: string | null;
  loading: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}

function ConfirmModal({ action, tiers, periodEnd, loading, onConfirm, onDismiss }: ConfirmModalProps) {
  const tierName = (id: string) => tiers.find((t) => t.id === id)?.name ?? id;
  const periodEndLabel = periodEnd ?? 'the end of your billing period';

  let title: string;
  let body: string;
  let confirmLabel: string;
  let destructive = false;

  if (action.type === 'upgrade') {
    title = `Upgrade to ${tierName(action.tierId)}`;
    body = `You'll be charged a prorated fee immediately and your annual minutes counter will reset. The upgrade takes effect as soon as payment confirms.`;
    confirmLabel = 'Upgrade';
  } else if (action.type === 'downgrade') {
    title = `Downgrade to ${tierName(action.tierId)}`;
    body = `Your plan will change to ${tierName(action.tierId)} on ${periodEndLabel}. Your current plan remains active until then.`;
    confirmLabel = 'Schedule downgrade';
  } else {
    title = 'Cancel plan';
    body = `Your account will be cancelled on ${periodEndLabel}. You can continue making calls until then and can reverse this before the period ends.`;
    confirmLabel = 'Cancel plan';
    destructive = true;
  }

  return (
    <ModalOverlay onClick={onDismiss}>
      <ModalDialog onClick={(e) => e.stopPropagation()}>
        <ModalTitle>{title}</ModalTitle>
        <ModalBody>{body}</ModalBody>
        <ModalActions>
          <SecondaryButton onClick={onDismiss} disabled={loading}>
            Never mind
          </SecondaryButton>
          {destructive ? (
            <DestructiveButton onClick={onConfirm} disabled={loading}>
              {loading ? 'Processing…' : confirmLabel}
            </DestructiveButton>
          ) : (
            <PrimaryButton onClick={onConfirm} disabled={loading}>
              {loading ? 'Processing…' : confirmLabel}
            </PrimaryButton>
          )}
        </ModalActions>
      </ModalDialog>
    </ModalOverlay>
  );
}

export function BillingPage() {
  const { data: status, loading: statusLoading } = useFetchData<BillingStatus | null>('/dashboard/api/billing/status', null);
  const { data: tiersData, loading: tiersLoading } = useFetchData<BillingTiers>('/dashboard/api/billing/tiers', { tiers: [] });
  const { data: usage, loading: usageLoading } = useFetchData<BillingUsage>('/dashboard/api/billing/usage?days=30', { days: [] });
  const { data: paymentsData } = useFetchData<BillingPayments>('/dashboard/api/billing/payments', { payments: [] });
  const [actionLoading, setActionLoading] = React.useState(false);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const [pendingAction, setPendingAction] = React.useState<PendingAction | null>(null);

  const loading = statusLoading || tiersLoading || usageLoading;

  const tierOrder = tiersData.tiers.map((t) => t.id);
  const currentIndex = tierOrder.indexOf(status?.billing_tier ?? '');

  const handleChangeTier = (tierId: string) => {
    const isUpgrade = tierOrder.indexOf(tierId) > currentIndex;
    setPendingAction({ type: isUpgrade ? 'upgrade' : 'downgrade', tierId });
  };

  const handleCancel = () => setPendingAction({ type: 'cancel' });

  const handleRevert = async () => {
    if (!status?.billing_tier) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const res = await fetch(`/dashboard/api/billing/change-tier?tier=${encodeURIComponent(status.billing_tier)}`, {
        method: "POST", headers: {[CSRF_HEADER_NAME]: await getCSRFToken()}
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setActionError(body.detail ?? 'Something went wrong. Please try again.');
      } else {
        window.location.reload();
      }
    } catch {
      setActionError('Network error. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!pendingAction) return;
    setActionLoading(true);
    setActionError(null);
    try {
      const url = pendingAction.type === 'cancel'
        ? '/dashboard/api/billing/cancel'
        : `/dashboard/api/billing/change-tier?tier=${encodeURIComponent((pendingAction as { tierId: string }).tierId)}`;
      const res = await fetch(url, {
        method: "POST", headers: {[CSRF_HEADER_NAME]: await getCSRFToken()}
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setActionError(body.detail ?? 'Something went wrong. Please try again.');
        setPendingAction(null);
      } else {
        const body = await res.json();
        if (body.redirect_url) window.location.href = body.redirect_url;
        else window.location.reload();
      }
    } catch {
      setActionError('Network error. Please try again.');
      setPendingAction(null);
    } finally {
      setActionLoading(false);
    }
  };

  const pendingTier = status?.pending_tier;
  const pendingIsUpgrade = pendingTier != null && pendingTier !== 'cancelled'
    && tierOrder.indexOf(pendingTier) > currentIndex;

  return (
    <div>
      {pendingAction && (
        <ConfirmModal
          action={pendingAction}
          tiers={tiersData.tiers}
          periodEnd={status?.period_end ?? null}
          loading={actionLoading}
          onConfirm={handleConfirm}
          onDismiss={() => setPendingAction(null)}
        />
      )}
      <PageHeader>
        <PageTitle>Billing</PageTitle>
      </PageHeader>

      {status?.status === 'suspended' && (
        <AlertBanner variant="red" style={{ marginBottom: 20 }}>
          Your account has been suspended due to a failed payment. Please update your payment method to restore access.
        </AlertBanner>
      )}
      {status?.status === 'past_due' && (
        <AlertBanner variant="amber" style={{ marginBottom: 20 }}>
          Your account has a past-due balance. Please update your payment method to avoid service interruption.
          {status.grace_period_end && (
            <> Access will be suspended after {new Date(status.grace_period_end).toLocaleDateString()}.</>
          )}
        </AlertBanner>
      )}
      {status?.minutes_used != null && status?.included_minutes != null &&
        status.minutes_used >= status.included_minutes * 0.8 &&
        status.minutes_used < status.included_minutes && (
        <AlertBanner variant="amber" style={{ marginBottom: 20 }}>
          You've used {pct(status.minutes_used, status.included_minutes)}% of your included minutes this year.
        </AlertBanner>
      )}
      {pendingTier && pendingIsUpgrade && (
        <AlertBanner variant="amber" style={{ marginBottom: 20 }}>
          Your upgrade to {tiersData.tiers.find((t) => t.id === pendingTier)?.name ?? pendingTier} is being processed.
        </AlertBanner>
      )}
      {pendingTier && !pendingIsUpgrade && pendingTier !== 'cancelled' && (
        <AlertBanner variant="amber" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>
            Your plan will change to {tiersData.tiers.find((t) => t.id === pendingTier)?.name ?? pendingTier}
            {status?.period_end ? ` on ${status.period_end}` : ' at the end of your billing period'}.
          </span>
          <button
            disabled={actionLoading}
            onClick={handleRevert}
            style={{ marginLeft: 16, fontSize: 12, color: 'inherit', background: 'none', border: '1px solid currentColor', borderRadius: 4, padding: '2px 8px', cursor: actionLoading ? 'default' : 'pointer', opacity: actionLoading ? 0.5 : 1, whiteSpace: 'nowrap' }}
          >
            Undo
          </button>
        </AlertBanner>
      )}
      {pendingTier === 'cancelled' && (
        <AlertBanner variant="amber" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>
            Your account will be cancelled{status?.period_end ? ` on ${status.period_end}` : ' at the end of your billing period'}. You can continue making calls until then.
          </span>
          <button
            disabled={actionLoading}
            onClick={handleRevert}
            style={{ marginLeft: 16, fontSize: 12, color: 'inherit', background: 'none', border: '1px solid currentColor', borderRadius: 4, padding: '2px 8px', cursor: actionLoading ? 'default' : 'pointer', opacity: actionLoading ? 0.5 : 1, whiteSpace: 'nowrap' }}
          >
            Undo cancellation
          </button>
        </AlertBanner>
      )}
      {actionError && (
        <AlertBanner variant="red" style={{ marginBottom: 20 }}>{actionError}</AlertBanner>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', padding: 16 }}>Loading…</div>
      ) : (
        <>
          <CardGrid cols={2} style={{ maxWidth: 860 }}>
            {status && <CurrentPlanCard status={status} tiers={tiersData.tiers} />}
            {status && <PaymentMethodCard hasPaymentMethod={status.payment_method_on_file} />}
          </CardGrid>

          {status?.billing_model === 'package' && (
            <TierTable
              tiers={tiersData.tiers}
              status={status}
              actionLoading={actionLoading}
              onChangeTier={handleChangeTier}
              onCancel={handleCancel}
            />
          )}

          {usage.days.length > 0 && <UsageHistory usage={usage} />}
          <PaymentHistory payments={paymentsData.payments} />
        </>
      )}
    </div>
  );
}
